import { useState, useEffect } from "react";

export default function MachineScan({ unitId, machineId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/machines/scan-summary/${unitId}/${machineId}`)
      .then(r => { if (!r.ok) throw new Error("Not found"); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [unitId, machineId]);

  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  if (error) return <div className="flex items-center justify-center min-h-screen text-red-500">Machine not found</div>;

  const { machine, recent_entries, total_entries } = data;

  // A blank row reads as a broken page; an em dash reads as "not recorded".
  const shown = v => (v === null || v === undefined || String(v).trim() === "") ? "—" : v;
  // Same formatting as the History Card, so the sticker and the screen never state
  // the same machine's dimensions two different ways. All parts must be present —
  // "800 × × 90" is worse than saying nothing was recorded.
  const dims = (...parts) => parts.every(p => String(p ?? "").trim())
    ? `${parts.join(" × ")} mm` : "—";

  return (
    <div className="min-h-screen bg-gray-50 p-4" data-testid="scan-page">
      <div className="max-w-lg mx-auto space-y-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-3 mb-3">
            <img src="/logo.jpg" alt="Logo" className="w-10 h-10 rounded-lg object-contain" />
            <div>
              <div className="font-black text-gray-900 text-base">Advanced Anmol Metcomp Pvt Ltd</div>
              <div className="text-xs text-gray-500">Machine Maintenance Record</div>
            </div>
          </div>
          <div className="border-t pt-3 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-gray-500">Machine No.</span><span className="font-mono font-bold text-blue-600" data-testid="scan-machine-no">{machine.machineNo}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-500">Name</span><span className="font-semibold">{machine.name}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-500">Department</span><span>{machine.dept}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-500">Capacity</span><span>{shown(machine.capacity)}</span></div>
            <div className="flex justify-between gap-3 text-sm">
              <span className="text-gray-500 shrink-0">Bed Size (L &times; W &times; H)</span>
              <span className="text-right" data-testid="scan-bed-size">{dims(machine.bedLength, machine.bedWidth, machine.bedHeight)}</span>
            </div>
            <div className="flex justify-between gap-3 text-sm">
              <span className="text-gray-500 shrink-0">Slide Size (L &times; W)</span>
              <span className="text-right" data-testid="scan-slide-size">{dims(machine.slideLength, machine.slideWidth)}</span>
            </div>
            <div className="flex justify-between text-sm"><span className="text-gray-500">Make</span><span>{shown(machine.make)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-500">Year</span><span>{shown(machine.year)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-500">Floor</span><span>{shown(machine.floor)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-gray-500">Status</span><span className={`px-2 py-0.5 rounded text-xs font-semibold ${machine.status === "Active" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>{machine.status}</span></div>
          </div>
        </div>

        <div className="bg-white rounded-xl border p-4">
          <h3 className="font-semibold text-gray-800 text-sm mb-3" data-testid="scan-entries-header">Recent Maintenance ({total_entries} total)</h3>
          {recent_entries.length === 0 ? (
            <p className="text-gray-400 text-sm">No maintenance records yet.</p>
          ) : (
            <div className="space-y-2">
              {recent_entries.map((e, i) => (
                <div key={i} className="border rounded-lg p-2 text-xs">
                  <div className="flex justify-between"><span className="font-medium text-gray-700">{e.description}</span><span className="text-gray-400">{e.startDate}</span></div>
                  {e.rectification && <div className="text-gray-500 mt-1">Fix: {e.rectification}</div>}
                  <div className="flex gap-2 mt-1">
                    <span className={`px-1.5 py-0.5 rounded text-xs ${e.workType === "internal" ? "bg-blue-50 text-blue-600" : "bg-orange-50 text-orange-600"}`}>{e.workType}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
